import React from 'react';
import { useSettings } from '../context/SettingsContext';

/**
 * Reusable pagination bar.
 *
 * Props:
 *   page        – current page (1-based)
 *   totalPages  – total number of pages
 *   total       – total number of items
 *   pageSize    – items per page
 *   onPage      – (newPage) => void
 */
export default function Pagination({ page, totalPages, total, pageSize, onPage }) {
  const { t } = useSettings();

  if (totalPages <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  // Build visible page numbers with ellipsis
  const pages = [];
  const delta = 1; // siblings on each side of current page
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= page - delta && i <= page + delta)
    ) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 12,
      padding: '14px 20px',
      borderTop: '1px solid var(--border)',
    }}>
      {/* Item count */}
      <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
        {from}–{to} / {total}
      </span>

      {/* Page buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {/* Prev */}
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          style={{
            padding: '5px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: page === 1 ? 'var(--text-dim)' : 'var(--text-muted)',
            fontSize: 13,
            cursor: page === 1 ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { if (page !== 1) e.currentTarget.style.background = 'var(--surface2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {t('prev')}
        </button>

        {/* Page numbers */}
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} style={{ padding: '5px 6px', color: 'var(--text-dim)', fontSize: 13 }}>…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPage(p)}
              style={{
                minWidth: 32,
                height: 32,
                borderRadius: 'var(--radius-sm)',
                border: p === page ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: p === page ? 'var(--accent-glow)' : 'transparent',
                color: p === page ? 'var(--accent)' : 'var(--text-muted)',
                fontSize: 13,
                fontWeight: p === page ? 600 : 400,
                cursor: p === page ? 'default' : 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (p !== page) e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { if (p !== page) e.currentTarget.style.background = 'transparent'; }}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => onPage(page + 1)}
          disabled={page === totalPages}
          style={{
            padding: '5px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: page === totalPages ? 'var(--text-dim)' : 'var(--text-muted)',
            fontSize: 13,
            cursor: page === totalPages ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { if (page !== totalPages) e.currentTarget.style.background = 'var(--surface2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {t('next')}
        </button>
      </div>
    </div>
  );
}
